import { useState } from "react";
import { Link } from "react-router-dom";
import { money, productImages, sized } from "@/lib/api";
import { Reveal } from "@/components/Reveal";

export const colorways = (p) => {
  const opt = (p?.options || []).find((o) => /colou?r/i.test(o.name));
  if (!opt) return [];
  const variants = Array.isArray(p?.variants?.nodes) ? p.variants.nodes : [];
  return opt.values
    .map((value) => {
      const v = variants.find((x) => x.image && x.selectedOptions.some((o) => o.name === opt.name && o.value === value));
      return v ? { value, image: v.image } : null;
    })
    .filter(Boolean);
};

const slug = (s) => s.toLowerCase().replace(/\s+/g, "-");

export const Featured = ({ p }) => {
  const ways = colorways(p);
  const [active, setActive] = useState(0);
  const main = ways[active]?.image || productImages(p)[0];
  const href = `/products/${p.handle}${ways[active] ? `?color=${encodeURIComponent(ways[active].value)}` : ""}`;
  return (
    <div data-testid="featured-product" className="grid gap-8 md:grid-cols-12 md:gap-10">
      <Reveal className="md:col-span-7">
        <Link to={href} className="group relative block aspect-[4/5] overflow-hidden bg-[#EEEBE5]">
          {ways.length
            ? ways.map((w, i) => (
                <img
                  key={w.value}
                  src={sized(w.image.url, 1000)}
                  alt={`${p.title} in ${w.value}`}
                  loading="lazy"
                  className={`absolute inset-0 h-full w-full object-contain p-[12%] mix-blend-multiply transition-[opacity,transform] duration-700 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-[1.03] ${i === active ? "opacity-100" : "opacity-0"}`}
                />
              ))
            : main && <img src={sized(main.url, 1000)} alt={p.title} loading="lazy" className="absolute inset-0 h-full w-full object-contain p-[12%] mix-blend-multiply" />}
          <span className="label absolute left-4 top-4 bg-[#F4F2EE] px-2 py-1 text-[10px] text-[#E2541B]">New</span>
          {ways[active] && <span data-testid="featured-active-color" className="label absolute bottom-4 left-4 text-[10px] text-[#111111]/70">{ways[active].value}</span>}
        </Link>
      </Reveal>
      <Reveal delay={0.1} className="flex flex-col justify-end md:col-span-5 md:pb-6">
        <p className="label text-[#111111]/60">Drop 01</p>
        <h3 className="mt-4 text-4xl font-bold uppercase leading-[0.9] tracking-[-0.05em] sm:text-5xl lg:text-6xl">{p.title}</h3>
        <p className="label mt-5 text-sm">{money(p.priceRange?.minVariantPrice)}</p>
        {ways.length > 1 && (
          <div className="mt-8">
            <p className="label text-[#111111]/60">{ways.length} colorways</p>
            <div className="mt-3 grid grid-cols-3 gap-2">
              {ways.map((w, i) => (
                <button
                  key={w.value}
                  type="button"
                  data-testid={`colorway-${slug(w.value)}`}
                  aria-pressed={i === active}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  className={`group block border text-left transition-colors duration-300 ${i === active ? "border-[#111111]" : "border-[#111111]/15 hover:border-[#111111]/50"}`}
                >
                  <span className="block aspect-square bg-[#EEEBE5]">
                    <img src={sized(w.image.url, 300)} alt="" loading="lazy" className="h-full w-full object-contain p-[10%] mix-blend-multiply" />
                  </span>
                  <span className="label block truncate px-2 py-2 text-[10px]">{w.value}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        <Link to={href} data-testid="featured-shop-button" className="label group relative mt-8 inline-flex h-14 w-full items-center justify-center overflow-hidden bg-[#111111] text-[#F4F2EE] md:w-64">
          <span className="absolute inset-0 origin-left scale-x-0 bg-[#E2541B] transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-x-100" />
          <span className="relative">Shop {ways[active] ? ways[active].value : "now"}</span>
        </Link>
      </Reveal>
    </div>
  );
};
